<template>
  <div class="preview-section mb-4">
    <div v-if="selectedElement" 
      :class="['card', { 'readonly-mode': readOnly }]"
    >
      <cardHeader 
        :numOrder="getQuestionNumber() === 'N/A' ? numOrder : getQuestionNumber()"
        :elementType="selectedElement.el_Type" 
        :mode="'Preview'"
        :isAgent="isAgent" />
      
      <div class="card-body">
        <!-- Main prompt -->
        <div class="mb-2">
          <h3>{{ selectedElement.el_Text }}</h3>
        </div>
        
        <!-- Rich text instructions -->
        <div v-show="!isQuillEmpty" class="quill-editor" ref="quillEditor"></div>

        <!-- Feedback containers -->
        <div class="AllFeedbacks" v-if="showReport">
          <div v-if="results.score == results.maxScore && selectedElement.feedback && selectedElement.feedback.correct"
            class="PositiveFeebackContainer">
            {{ selectedElement.feedback.correct }}
          </div>
          <div v-else-if="results.score < results.maxScore && selectedElement.feedback && selectedElement.feedback.incorrect"
            class="NegativeFeebackContainer">
            {{ selectedElement.feedback.incorrect }}
          </div>
        </div>

        <!-- Text with gaps -->
        <div class="puzzle-text-container mb-4 mt-4">
          <div 
            class="puzzle-text" 
            v-html="getTextWithGaps()"
            @drop="handleDrop"
            @dragover="handleDragOver"
            @dragleave="handleDragLeave"
          ></div>
        </div>

        <!-- Draggable elements -->
        <div class="drag-elements-container" v-if="shuffledElements.length > 0">
          <h6 class="mb-3">
            <i class="fas fa-hand-point-right me-2"></i>
            {{ $t('dragInstruction') || 'Drag these pieces to fill the gaps:' }}
          </h6>
          <div class="drag-elements" ref="dragContainer">
            <div
              v-for="element in shuffledElements"
              :key="element.id"
              class="drag-element"
              :class="{ 'disabled': placedElements.includes(element.id) }"
              draggable="true"
              @dragstart="handleDragStart($event, element)"
              @dragend="handleDragEnd"
              :data-drag = "element.id"
            >
              {{ element.text }}
            </div>
          </div>
        </div>

        <!-- Progress indicator -->
        <div class="progress-container mt-3" v-if="(selectedElement.el_InteractionData?.gaps || selectedElement.gaps || []).length > 0">
          <div class="d-flex justify-content-between align-items-center">
            <span class="text-muted small">
              {{ $t('progress') || 'Progress' }}: {{ filledGaps }} / {{ (selectedElement.el_InteractionData?.gaps || selectedElement.gaps || []).length }}
            </span>
            <div class="progress" style="width: 200px;">
              <div 
                class="progress-bar" 
                :style="{ width: progressPercentage + '%' }"
                :class="progressPercentage === 100 ? 'bg-success' : 'bg-primary'"
              ></div>
            </div>
          </div>
        </div>
      </div>

      <!-- Report Container -->
      <div class="ReportContainer" v-if="showReport">
        <div class="row">
          <div class="col">
            <h5>Results:</h5>
          </div>
          <div class="col" style="text-align: right;"><strong>Date:</strong> {{ currentDate }}</div>
        </div>
        
        <hr>
        
        <!-- Gap-by-gap results -->
        <div class="mb-3">
          <strong>Gap Results:</strong>
          <div v-for="(gap, index) in (selectedElement.el_InteractionData?.gaps || selectedElement.gaps || [])" :key="gap.id" class="gap-result-item">
            <span class="gap-number">Gap {{ index + 1 }}:</span>
            <span class="gap-text">"{{ gap.text }}"</span>
            <span class="gap-status">
              <span v-if="gapContents[gap.id] === gap.text" class="success">✅ Correct</span>
              <span v-else-if="gapContents[gap.id]" class="error">❌ Incorrect ({{ gapContents[gap.id] }})</span>
              <span v-else class="missing">⭕ Not filled</span>
            </span>
          </div>
        </div>
        
        <!-- Summary -->
        <p><strong>Score:</strong> {{ results.score }}</p>
        <p><strong>Max-Score:</strong> {{ results.maxScore }}</p>
        <p><strong>Penalties:</strong> {{ results.penalties }}</p>
        <p><strong>Completion Rate:</strong> {{ results.completionRate }}%</p>
        <p><strong>Correct Gaps:</strong> {{ results.correctGaps }} / {{ (selectedElement.el_InteractionData?.gaps || selectedElement.gaps || []).length }}</p>
      </div>

      <!-- Card Footer for Learning Mode -->
      <cardFooterLearningMode 
        v-if="(publicationStore.publicationData?.mode) !== 'exam'"
        :showReport="showReport" 
        :submitAnswers="showReportOnSubmit" 
        :closeReport="closeReport"
        :resetSelections="resetSelections" />
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted, watch, nextTick } from 'vue'
import { useResponsesStore } from '../../stores/useResponsesStore.js'
import { usePublicationStore } from '@/stores/usePublicationStore'
import cardHeader from './cardHeader.vue'
import cardFooterLearningMode from './cardFooterLearningMode.vue'
import Quill from 'quill'

export default {
  name: 'TextPuzzleRender',
  components: {
    cardHeader,
    cardFooterLearningMode
  },
  props: {
    question: {
      type: Object,
      required: true
    },
    testData: {
      type: Object,
      required: true
    },
    numOrder: [Number, null],
    questionIndexMap: {
      type: Object,
      required: false
    },
    response: {
      type: [Object, Boolean, null],
      default: () => ({})
    },
    restoredAnswer: {
      type: [Object, Array, Boolean, null],
      default: null
    },
    readOnly: {
      type: Boolean,
      default: false
    },
    isSubmitted: {
      type: Boolean,
      default: false
    },
    showCorrection: {
      type: Boolean,
      default: false
    }
  },
  emits: ['update:response'],
  setup(props, { emit }) {
    const responsesStore = useResponsesStore()
    const publicationStore = usePublicationStore()
    
    // Récupérer userId depuis le token
    const token = localStorage.getItem('token')
    const decodedToken = token ? JSON.parse(atob(token.split('.')[1])) : null
    const userId = decodedToken?._id || decodedToken?.sub
    const isAgent = ref(decodedToken?.agent === true)
    const agentLevel = ref(decodedToken?.agentLevel || null)
    
    const availableElements = ref([])
    const placedElements = ref([])
    const gapContents = ref({})
    const draggedElement = ref(null)
    const dragContainer = ref(null)
    const quillEditor = ref(null)
    
    // Quill setup
    let quillInstance = null
    let isQuillEmpty = ref(true)
    
    // Report functionality
    const showReport = ref(false)
    const results = ref({
      score: 0,
      maxScore: 0,
      penalties: 0,
      completionRate: 0,
      correctGaps: 0
    })

    // Make selectedElement point to question for compatibility
    const selectedElement = computed(() => props.question)
    const showCorrection = computed(() => props.showCorrection)

    // Question numbering
    const getQuestionNumber = () => {
      if (!props.questionIndexMap || typeof props.questionIndexMap !== 'object') {
        console.error("❌ questionIndexMap est indéfini ou n'est pas un objet.")
        return "N/A"
      }
      return props.questionIndexMap[props.question.el_ID] ?? "N/A"
    }

    const filledGaps = computed(() => {
      return Object.keys(gapContents.value).length
    })

    const progressPercentage = computed(() => {
      const gaps = props.question.el_InteractionData?.gaps || props.question.gaps || []
      if (!gaps.length) return 0
      return (filledGaps.value / gaps.length) * 100
    })

    const shuffledElements = computed(() => {
      return availableElements.value
    })

    const initializeElements = () => {
      // Check multiple possible locations for drag elements
      const elements = [...(props.question.el_InteractionData?.dragElements || 
                           props.question.dragElements || [])]
      
      console.log('TextPuzzle dragElements found:', elements)
      console.log('TextPuzzle dragElements count:', elements.length)
      
      // Always shuffle elements for better user experience
      for (let i = elements.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1))
        ;[elements[i], elements[j]] = [elements[j], elements[i]]
      }

      availableElements.value = elements
    }

    const getTextWithGaps = () => {
      //console.log('TextPuzzle getTextWithGaps - question structure:', props.question)
      
      // Check multiple possible locations for source text
      const sourceText = props.question.el_SourceText || 
                        props.question.el_InteractionData?.el_SourceText ||
                        props.question.el_InteractionData?.sourceText

      //console.log('TextPuzzle found sourceText:', sourceText)

      if (!sourceText) {
        console.warn('No source text found in:', props.question)
        return '<p>No text content available</p>'
      }

      let content = sourceText
      const gaps = props.question.el_InteractionData?.gaps || 
                  props.question.gaps || []
      
      console.log('TextPuzzle gaps found:', gaps)
      console.log('TextPuzzle gap count:', gaps.length)
      

      // Replace gap placeholders with actual gap elements
      gaps.forEach((gap, index) => {
        const gapNumber = index + 1
        const gapPlaceholder = `[GAP_${gapNumber}]`
        
        // Use original ID if available, otherwise create stable fallback
        const gapId = gap.id || `gap_fallback_${index}_${gap.text?.substring(0,5)?.replace(/\s/g,'')}`
        const gapContent = gapContents.value[gapId] || ''
        
        
        let gapClass = 'text-gap'
        if (gapContent) {
          gapClass += ' filled'
          if (showCorrection.value) {
            const isCorrect = gapContent === gap.text
            gapClass += isCorrect ? ' correct' : ' incorrect'
          }
        } else {
          gapClass += ' empty'
        }
        
        const gapElement = `
          <span 
            class="${gapClass}" 
            data-gap-id="${gapId}"
            data-gap-index="${index}"
          >
            ${gapContent || `[Drop here ${gapNumber}]`}
          </span>
        `
        
        content = content.replace(gapPlaceholder, gapElement)
      })

      return content
    }

    const handleDragStart = (event, element) => {
      // Always allow repositioning for better user experience
      draggedElement.value = element
      event.dataTransfer.setData('text/plain', element.id)
      event.dataTransfer.effectAllowed = 'move'
      
      // Add dragging class
      event.target.classList.add('dragging')
    }

    const handleDragEnd = (event) => {
      event.target.classList.remove('dragging')
      
      // Remove drag-over highlighting from all gaps
      document.querySelectorAll('.text-gap').forEach(gap => {
        gap.classList.remove('drag-over')
      })
    }

    const handleDragOver = (event) => {
      event.preventDefault()
      event.dataTransfer.dropEffect = 'move'
      
      // Highlight valid drop zones
      const target = event.target
      if (target.classList.contains('text-gap')) {
        target.classList.add('drag-over')
      }
    }

    const handleDragLeave = (event) => {
      const target = event.target
      if (target.classList.contains('text-gap')) {
        target.classList.remove('drag-over')
      }
    }

    const handleDrop = (event) => {
      event.preventDefault()
      const target = event.target


      // Remove drag-over highlighting
      document.querySelectorAll('.text-gap').forEach(gap => {
        gap.classList.remove('drag-over')
      })

      // Only allow drops on gap elements
      if (!target.classList.contains('text-gap')) {
        return
      }

      const gapId = target.getAttribute('data-gap-id')
      const elementId = event.dataTransfer.getData('text/plain')
      const element = availableElements.value.find(el => el.id === elementId)

      if (element && gapId) {
        // Always allow repositioning for better user experience
        Object.keys(gapContents.value).forEach(key => {
          if (gapContents.value[key] === element.text) {
            delete gapContents.value[key]
          }
        })
        placedElements.value = placedElements.value.filter(id => id !== elementId)

        // Place element in new gap
        gapContents.value[gapId] = element.text
        if (!placedElements.value.includes(elementId)) {
          placedElements.value.push(elementId)
        }

        // Always show visual feedback for better user experience
        target.classList.add('drop-success')
        setTimeout(() => {
          target.classList.remove('drop-success')
        }, 600)

        // Update response
        updateResponse()
      }
    }

    const updateResponse = () => {
      const response = {
        userId: userId,
        questionId: props.question.el_ID,
        type: props.question.el_Type,
        questionLabel: props.question.el_Text,
        gapAnswers: { ...gapContents.value },
        timestamp: new Date().toISOString(),
        answered: Object.keys(gapContents.value).length > 0,
        maxScore: (props.question.el_InteractionData?.gaps || props.question.gaps || []).length * (props.question.el_InteractionData?.pointsPerCorrect || props.question.pointsPerCorrect || 1) || 0,
        score: calculateCurrentScore()
      }
      
      // Sauvegarde dans le store Pinia
      responsesStore.saveResponse(response)
      
      // Emit pour compatibilité
      emit('update:response', response)
    }

    const calculateCurrentScore = () => {
      const gaps = props.question.el_InteractionData?.gaps || props.question.gaps || []
      const pointsPerCorrect = props.question.el_InteractionData?.pointsPerCorrect || props.question.pointsPerCorrect || 1
      const penaltyPerIncorrect = props.question.el_InteractionData?.penaltyPerIncorrect || props.question.penaltyPerIncorrect || 0
      
      let score = 0
      gaps.forEach(gap => {
        const gapId = gap.id || `gap_fallback_${gaps.indexOf(gap)}_${gap.text?.substring(0,5)?.replace(/\s/g,'')}`
        const userAnswer = gapContents.value[gapId]
        if (userAnswer) {
          if (userAnswer === gap.text) {
            score += pointsPerCorrect
          } else {
            score -= penaltyPerIncorrect
          }
        }
      })
      
      return Math.max(0, score)
    }

    const loadResponse = () => {
      // D'abord essayer de charger depuis les props (seulement si c'est un objet)
      if (props.response && typeof props.response === 'object' && props.response.gapAnswers) {
        gapContents.value = { ...props.response.gapAnswers }
      } else if (props.restoredAnswer && typeof props.restoredAnswer === 'object' && props.restoredAnswer.gapAnswers) {
        gapContents.value = { ...props.restoredAnswer.gapAnswers }
      } else {
        // Sinon essayer de restaurer depuis le store
        const savedResponse = responsesStore.getResponseForQuestion(userId, props.question.el_ID)
        if (savedResponse && savedResponse.gapAnswers) {
          gapContents.value = { ...savedResponse.gapAnswers }
        }
      }
      
      // Update placed elements list
      Object.values(gapContents.value).forEach(answer => {
        const element = availableElements.value.find(el => el.text === answer)
        if (element && !placedElements.value.includes(element.id)) {
          placedElements.value.push(element.id)
        }
      })
    }

    onMounted(() => {
      initializeElements()
      loadResponse()
      
      // Initialize Quill for rich text display
      if (quillEditor.value) {
        quillInstance = new Quill(quillEditor.value, {
          theme: 'snow',
          readOnly: true,
          modules: {
            toolbar: false
          },
        })

        if (props.question?.el_RichText) {
          quillInstance.root.innerHTML = props.question.el_RichText
        }

        // Check if Quill content is empty
        const plainText = props.question?.el_RichText?.replace(/<[^>]*>?/gm, '').trim() || ''
        isQuillEmpty.value = plainText.length === 0
      }
      
      // Auto-réponse pour les agents après un délai pour laisser l'interface se charger
      if (isAgent.value && agentLevel.value) {
        setTimeout(() => {
          autoAnswerForAgent()
        }, 1500) // Délai pour laisser l'interface se charger
      }
    })

    watch(() => props.response, () => {
      loadResponse()
    }, { deep: true })

    watch(() => props.question, () => {
      console.log('Question changed, reinitializing elements...')
      initializeElements()
    }, { deep: true })

    // Debug watch for availableElements
    watch(availableElements, (newElements) => {
      console.log('availableElements changed:', newElements)
    }, { deep: true })

    // Date for report
    const currentDate = computed(() => {
      const now = new Date()
      return now.toLocaleString("en-US", {
        weekday: "long",
        year: "numeric",
        month: "long",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
      })
    })

    // Calculate detailed results
    const calculateResults = () => {
      const gaps = props.question.el_InteractionData?.gaps || props.question.gaps || []
      const pointsPerCorrect = props.question.el_InteractionData?.pointsPerCorrect || props.question.pointsPerCorrect || 1
      const penaltyPerIncorrect = props.question.el_InteractionData?.penaltyPerIncorrect || props.question.penaltyPerIncorrect || 0
      
      let score = 0
      let penalties = 0
      let correctGaps = 0
      
      gaps.forEach(gap => {
        const userAnswer = gapContents.value[gap.id]
        if (userAnswer) {
          if (userAnswer === gap.text) {
            score += pointsPerCorrect
            correctGaps++
          } else {
            penalties += penaltyPerIncorrect
          }
        }
      })

      const maxScore = gaps.length * pointsPerCorrect
      const completionRate = gaps.length > 0 ? (Object.keys(gapContents.value).length / gaps.length) * 100 : 100

      results.value = {
        score: Math.max(0, score - penalties),
        maxScore,
        penalties,
        completionRate: Math.round(completionRate),
        correctGaps
      }
    }

    // Submit answers and show report
    const showReportOnSubmit = () => {
      calculateResults()
      showReport.value = true
    }

    // Close report
    const closeReport = () => {
      showReport.value = false
    }

    // Fonction pour auto-réponse basée sur l'agentLevel pour l'interaction TextPuzzle
    const autoAnswerForAgent = () => {
      if (!isAgent.value || !agentLevel.value) return

      const gaps = props.question.el_InteractionData?.gaps || props.question.gaps || []
      const dragElements = props.question.el_InteractionData?.dragElements || props.question.dragElements || []

      if (gaps.length === 0 || dragElements.length === 0) return

      // Séparer les éléments corrects et les distracteurs
      const correctElements = dragElements.filter(el => el.isCorrect !== false)
      const distractorElements = dragElements.filter(el => el.isCorrect === false)
      const wrongElements = [...distractorElements, ...correctElements.filter(el => gaps.every(gap => gap.text !== el.text))]

      if (agentLevel.value === 'A') {
        // Niveau A: placer tous les éléments dans les bons gaps (85-100% succès)
        gaps.forEach((gap, index) => {
          const gapId = gap.id || `gap_fallback_${index}_${gap.text?.substring(0,5)?.replace(/\s/g,'')}`

          // Trouver l'élément correspondant au texte attendu du gap
          const correctElement = correctElements.find(element => element.text === gap.text)
          if (correctElement) {
            setTimeout(() => {
              gapContents.value[gapId] = correctElement.text
              if (!placedElements.value.includes(correctElement.id)) {
                placedElements.value.push(correctElement.id)
              }
              updateResponse()
            }, index * 300) // Délai progressif pour voir les éléments se placer
          }
        })
      } else if (agentLevel.value === 'B') {
        // Niveau B: taux de succès ciblé 40-84% (moyenne ~62%)
        gaps.forEach((gap, index) => {
          const gapId = gap.id || `gap_fallback_${index}_${gap.text?.substring(0,5)?.replace(/\s/g,'')}`

          // 38% de chances de placer un élément incorrect
          const shouldGiveWrongAnswer = Math.random() < 0.38
          let elementToPlace

          if (shouldGiveWrongAnswer && wrongElements.length > 0) {
            // Placer un élément incorrect (distracteur ou mauvais élément correct)
            const randomIndex = Math.floor(Math.random() * wrongElements.length)
            elementToPlace = wrongElements[randomIndex]
          } else {
            // Placer le bon élément
            elementToPlace = correctElements.find(element => element.text === gap.text)
          }

          if (elementToPlace) {
            setTimeout(() => {
              gapContents.value[gapId] = elementToPlace.text
              if (!placedElements.value.includes(elementToPlace.id)) {
                placedElements.value.push(elementToPlace.id)
              }
              updateResponse()
            }, index * 300)
          }
        })
      } else if (agentLevel.value === 'C') {
        // Niveau C: taux de succès ciblé <40% (moyenne ~20%)
        gaps.forEach((gap, index) => {
          const gapId = gap.id || `gap_fallback_${index}_${gap.text?.substring(0,5)?.replace(/\s/g,'')}`

          // 80% de chances de placer un élément incorrect
          const shouldGiveWrongAnswer = Math.random() < 0.8
          let elementToPlace

          if (shouldGiveWrongAnswer && wrongElements.length > 0) {
            // Placer un élément incorrect (distracteur ou mauvais élément correct)
            const randomIndex = Math.floor(Math.random() * wrongElements.length)
            elementToPlace = wrongElements[randomIndex]
          } else {
            // Placer le bon élément
            elementToPlace = correctElements.find(element => element.text === gap.text)
          }

          if (elementToPlace) {
            setTimeout(() => {
              gapContents.value[gapId] = elementToPlace.text
              if (!placedElements.value.includes(elementToPlace.id)) {
                placedElements.value.push(elementToPlace.id)
              }
              updateResponse()
            }, index * 300)
          }
        })
      }
    }

    // Reset all selections
    const resetSelections = () => {
      gapContents.value = {}
      placedElements.value = []
      showReport.value = false
      updateResponse()
    }

    return {
      selectedElement,
      availableElements,
      shuffledElements,
      placedElements,
      dragContainer,
      quillEditor,
      isQuillEmpty,
      filledGaps,
      progressPercentage,
      getTextWithGaps,
      handleDragStart,
      handleDragEnd,
      handleDragOver,
      handleDragLeave,
      handleDrop,
      getQuestionNumber,
      // Props for template
      readOnly: computed(() => props.readOnly),
      numOrder: computed(() => props.numOrder),
      showCorrection,
      // Report functionality
      showReport,
      results,
      currentDate,
      showReportOnSubmit,
      closeReport,
      resetSelections,
      gapContents,
      publicationStore,
      // Agent functionality
      isAgent,
      agentLevel
    }
  }
}
</script>

<style scoped>
.text-puzzle-render {
  font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
}

.puzzle-text-container {
  background-color: #f8f9fa;
  padding: 25px;
  border-radius: 12px;
  border: 2px solid #e9ecef;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

.puzzle-text {
  font-size: 1.15em;
  line-height: 1.8;
  color: #495057;
}

.drag-elements {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  padding: 20px;
  background-color: #e7f3ff;
  border-radius: 12px;
  min-height: 80px;
  border: 2px dashed #0066cc;
  transition: all 0.3s ease;
}

.drag-elements:empty::after {
  content: "All pieces have been placed!";
  color: #28a745;
  font-weight: 600;
  font-style: italic;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 40px;
}

.drag-element {
  background: linear-gradient(135deg, #007bff, #0056b3);
  color: white;
  padding: 10px 16px;
  border-radius: 8px;
  cursor: grab;
  user-select: none;
  font-weight: 500;
  transition: all 0.3s ease;
  box-shadow: 0 3px 6px rgba(0,0,0,0.16);
  position: relative;
  border: 2px solid transparent;
}

.drag-element:hover:not(.disabled) {
  background: linear-gradient(135deg, #0056b3, #004085);
  transform: translateY(-2px);
  box-shadow: 0 5px 15px rgba(0,0,0,0.2);
}

.drag-element:active:not(.disabled) {
  cursor: grabbing;
  transform: scale(0.98) translateY(-1px);
}

.drag-element.dragging {
  opacity: 0.7;
  transform: rotate(5deg) scale(1.05);
  z-index: 1000;
}

.drag-element.disabled {
  opacity: 0.4;
  background: linear-gradient(135deg, #6c757d, #545b62);
  cursor: not-allowed;
  transform: none !important;
}

:deep(.text-gap) {
  display: inline-block;
  min-width: 120px;
  min-height: 32px;
  padding: 6px 12px;
  margin: 0 3px;
  border: 2px dashed #007bff;
  border-radius: 6px;
  background-color: white;
  color: #007bff;
  font-weight: 600;
  text-align: center;
  vertical-align: baseline;
  transition: all 0.3s ease;
  cursor: default;
  box-shadow: inset 0 1px 3px rgba(0,0,0,0.1);
}

:deep(.text-gap.empty) {
  color: #6c757d;
  font-style: italic;
  font-size: 0.85em;
  font-weight: 400;
  background: repeating-linear-gradient(
    45deg,
    #f8f9fa,
    #f8f9fa 5px,
    #ffffff 5px,
    #ffffff 10px
  );
}

:deep(.text-gap.filled) {
  background-color: #d4edda;
  border-color: #28a745;
  color: #155724;
  box-shadow: 0 2px 4px rgba(40, 167, 69, 0.2);
}

:deep(.text-gap.drag-over) {
  background-color: #cce5ff;
  border-color: #0066cc;
  transform: scale(1.08);
  box-shadow: 0 4px 12px rgba(0, 102, 204, 0.3);
  animation: pulse 0.5s ease-in-out infinite alternate;
}

:deep(.text-gap.drop-success) {
  animation: dropSuccess 0.6s ease;
}

:deep(.text-gap.correct) {
  background-color: #d1e7dd;
  border-color: #198754;
  color: #0f5132;
  box-shadow: 0 2px 6px rgba(25, 135, 84, 0.3);
}

:deep(.text-gap.incorrect) {
  background-color: #f8d7da;
  border-color: #dc3545;
  color: #721c24;
  box-shadow: 0 2px 6px rgba(220, 53, 69, 0.3);
  position: relative;
}

:deep(.text-gap.incorrect::after) {
  content: " ✗";
  color: #dc3545;
  font-weight: bold;
}

:deep(.text-gap.correct::after) {
  content: " ✓";
  color: #198754;
  font-weight: bold;
}

@keyframes dropSuccess {
  0% { 
    background-color: #d4edda; 
    transform: scale(1.08);
  }
  50% { 
    background-color: #90ee90; 
    transform: scale(1.15);
  }
  100% { 
    background-color: #d4edda; 
    transform: scale(1);
  }
}

@keyframes pulse {
  from { box-shadow: 0 4px 12px rgba(0, 102, 204, 0.3); }
  to { box-shadow: 0 6px 20px rgba(0, 102, 204, 0.5); }
}

.progress-container {
  background-color: #f8f9fa;
  padding: 15px;
  border-radius: 8px;
  border: 1px solid #dee2e6;
}

.progress {
  height: 8px;
  border-radius: 4px;
  background-color: #e9ecef;
  overflow: hidden;
}

.progress-bar {
  transition: width 0.4s ease;
  border-radius: 4px;
}

/* Report Container Styles */
.ReportContainer {
  border: grey 1px solid;
  border-radius: 3px;
  padding: 20px;
  background-color: rgb(54, 63, 67);
  color: greenyellow;
  margin-top: 20px;
}

.ReportContainer h5 {
  color: greenyellow;
  margin-bottom: 15px;
}

.ReportContainer hr {
  border-color: #6c757d;
}

.ReportContainer p {
  margin-bottom: 8px;
  font-family: 'Courier New', monospace;
}

.gap-result-item {
  display: flex;
  align-items: center;
  margin-bottom: 8px;
  padding: 5px;
  background-color: rgba(0, 0, 0, 0.2);
  border-radius: 4px;
  font-family: 'Courier New', monospace;
}

.gap-number {
  min-width: 80px;
  font-weight: bold;
  color: #ffd700;
}

.gap-text {
  flex: 1;
  margin: 0 10px;
  font-style: italic;
  color: #87ceeb;
}

.gap-status {
  min-width: 150px;
  text-align: right;
}

.gap-status .success {
  color: #28a745;
  font-weight: bold;
}

.gap-status .error {
  color: #dc3545;
  font-weight: bold;
}

.gap-status .missing {
  color: #ffc107;
  font-weight: bold;
}

/* Quill Editor Styles */
.quill-editor {
  height: auto !important;
  border-radius: 5px;
  overflow: hidden !important;
  transition: height 0.3s ease;
  padding: 5px;
  border: none;
}


  /* Cacher l'élément clipboard de Quill */
  .quill-editor :deep(.ql-clipboard) {
  display: none !important;
}

/* Feedback Containers */
.PositiveFeebackContainer {
  background-color: rgb(222, 246, 224);
  margin: 10px 0;
  padding: 10px;
  border: 1px green dotted;
  border-radius: 5px;
}

.NegativeFeebackContainer {
  background-color: rgb(246, 234, 235);
  margin: 10px 0;
  padding: 10px;
  border: 1px rgb(253, 97, 206) dotted;
  border-radius: 5px;
}

/* Read-only mode styling */
.readonly-mode {
  background-color: #f8f9fa;
  opacity: 0.8;
  pointer-events: none;
  cursor: not-allowed;
}

@media (max-width: 768px) {
  .puzzle-text {
    font-size: 1.05em;
    line-height: 1.6;
  }
  
  .drag-element {
    padding: 8px 12px;
    font-size: 0.9em;
  }
  
  :deep(.text-gap) {
    min-width: 100px;
    min-height: 28px;
    padding: 4px 8px;
    font-size: 0.9em;
  }

}
</style>